/**
 * My Events Page
 * User's created events and events they've RSVP'd to
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Tabs,
  Tab,
  Pagination,
  Stack,
} from '@mui/material'
import { Add, Event, CalendarMonth, CheckCircle } from '@mui/icons-material'
import { useMyEvents } from '../hooks/useCalendarEvents'
import { DEFAULT_EVENT_PAGE_SIZE } from '@api/types/calendarEvent'
import { CalendarEventCard, CalendarEventCardSkeleton } from '../components/CalendarEventCard'
import EventSubscriptionBanner, { useCanCreateEvent } from '../components/EventSubscriptionBanner'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  )
}

export function MyEventsPage() {
  const [tab, setTab] = useState(0)
  const [page, setPage] = useState(1)

  const { canCreate } = useCanCreateEvent()

  // Fetch user's events
  // Tab 0: All my events, Tab 1: Events I'm attending
  const { data, isLoading } = useMyEvents({
    page,
    pageSize: DEFAULT_EVENT_PAGE_SIZE,
    includeRsvps: tab === 1,
  })

  const events = data?.items || []
  const totalPages = data?.totalPages || 1
  const totalCount = data?.totalCount || 0

  // Filter based on tab
  // For now, filter client-side. Ideally API would support this filter
  const filteredEvents = tab === 0
    ? events // All my events (created by me)
    : events.filter((e) => e.userRsvp) // Events I've RSVP'd to

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
    setPage(1)
  }

  // Handle page change
  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              <CalendarMonth sx={{ mr: 1, verticalAlign: 'middle' }} />
              My Events
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your events and RSVPs
            </Typography>
          </Box>
          <Button
            component={Link}
            to="/events/create"
            variant="contained"
            startIcon={<Add />}
            disabled={!canCreate}
          >
            Create Event
          </Button>
        </Stack>
      </Box>

      {/* Subscription Banner */}
      <EventSubscriptionBanner />

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab
            icon={<Event />}
            iconPosition="start"
            label="My Events"
          />
          <Tab
            icon={<CheckCircle />}
            iconPosition="start"
            label="Attending"
          />
        </Tabs>
      </Box>

      {/* My Events Tab */}
      <TabPanel value={tab} index={0}>
        {isLoading ? (
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <CalendarEventCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : filteredEvents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Event sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No events created yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first event and start gathering attendees!
            </Typography>
            {canCreate && (
              <Button
                component={Link}
                to="/events/create"
                variant="contained"
                startIcon={<Add />}
              >
                Create Event
              </Button>
            )}
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {totalCount} event{totalCount !== 1 ? 's' : ''}
            </Typography>
            <Grid container spacing={3}>
              {filteredEvents.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <CalendarEventCard event={event} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </TabPanel>

      {/* Attending Tab */}
      <TabPanel value={tab} index={1}>
        {isLoading ? (
          <Grid container spacing={3}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <CalendarEventCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : filteredEvents.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No RSVPs yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Browse events and RSVP to see them here!
            </Typography>
            <Button
              component={Link}
              to="/events"
              variant="contained"
            >
              Browse Events
            </Button>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} you're attending
            </Typography>
            <Grid container spacing={3}>
              {filteredEvents.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <CalendarEventCard event={event} />
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </TabPanel>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}
    </Container>
  )
}

export default MyEventsPage
